import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX, RotateCcw, Sparkles, Trophy } from "lucide-react";
import {
    BOARD_SIZE,
    makeEmptyBoard,
    canPlace,
    placeShape,
    findFullLines,
    getClearCoords,
    applyClears,
    hasAnyValidMove,
    pieceFitsAnywhere,
    computeScore,
} from "../lib/gameLogic";
import { createTray } from "../lib/trayGenerator";
import { audio } from "../lib/audio";

const LS_BEST = "blastnova_best_score";
const LS_NAME = "blastnova_nickname";
const LS_MUTE = "blastnova_muted";
// Legacy keys (pre-rename) — read once for migration, then ignore.
const LEGACY_BEST = "blocknova_best_score";
const LEGACY_NAME = "blocknova_nickname";
const LEGACY_MUTE = "blocknova_muted";

/** Reads gap/padding from `.bn-board` computed style (inherits `--bn-board-*` from `.bn-board-shell`). */
function readBoardGapPadding(boardEl) {
    if (!boardEl) return { gap: 4, padding: 10 };
    const cs = getComputedStyle(boardEl);
    const gap = parseFloat(cs.columnGap || cs.rowGap || cs.gap || "4") || 4;
    const padding = parseFloat(cs.paddingLeft || cs.paddingTop || "10") || 10;
    return { gap, padding };
}

function readWithLegacy(primary, legacy) {
    const v = localStorage.getItem(primary);
    if (v !== null) return v;
    const lv = localStorage.getItem(legacy);
    if (lv !== null) {
        // Migrate and clean up the legacy key so localStorage doesn't grow stale.
        localStorage.setItem(primary, lv);
        localStorage.removeItem(legacy);
        return lv;
    }
    return null;
}

const ADJECTIVES = [
    "Neon",
    "Cosmic",
    "Pixel",
    "Vivid",
    "Glow",
    "Cyber",
    "Stellar",
    "Volt",
    "Echo",
    "Plasma",
];
const NOUNS = [
    "Falcon",
    "Tiger",
    "Drifter",
    "Wizard",
    "Comet",
    "Phantom",
    "Wave",
    "Pulse",
    "Nomad",
    "Sage",
];

function generateNickname() {
    const a = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
    const n = NOUNS[Math.floor(Math.random() * NOUNS.length)];
    const num = Math.floor(10 + Math.random() * 90);
    return `${a}${n}${num}`;
}

// --- Tray piece (in slot) ---
const TrayPiece = ({ piece, onPointerDown, deadlocked, cellSize = 22 }) => {
    if (!piece) {
        return <div className="bn-tray-piece is-empty" />;
    }
    const { cells, theme } = piece;
    return (
        <div
            className={`bn-tray-piece${deadlocked ? " is-deadlocked" : ""}`}
            style={{
                gridTemplateColumns: `repeat(${cells[0].length}, ${cellSize}px)`,
                gridTemplateRows: `repeat(${cells.length}, ${cellSize}px)`,
            }}
            onPointerDown={(e) => onPointerDown(e, piece)}
            data-testid={`tray-piece-${piece.uid}`}
        >
            {cells.map((row, r) =>
                row.map((v, c) => (
                    <div
                        key={`${r}-${c}`}
                        className="bn-tray-piece-cell"
                        style={{
                            visibility: v ? "visible" : "hidden",
                            "--cell-color": theme.color,
                            "--cell-glow": theme.glow,
                        }}
                    />
                ))
            )}
        </div>
    );
};

// --- Floating drag preview ---
const DragPreview = ({ piece, position, cellSize }) => {
    if (!piece) return null;
    const { cells, theme } = piece;
    return (
        <div className="bn-drag-layer" aria-hidden>
            <div
                className="bn-drag-piece"
                style={{
                    transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
                    gridTemplateColumns: `repeat(${cells[0].length}, ${cellSize}px)`,
                    gridTemplateRows: `repeat(${cells.length}, ${cellSize}px)`,
                }}
            >
                {cells.map((row, r) =>
                    row.map((v, c) => (
                        <div
                            key={`${r}-${c}`}
                            className="bn-drag-piece-cell"
                            style={{
                                visibility: v ? "visible" : "hidden",
                                "--cell-color": theme.color,
                                "--cell-glow": theme.glow,
                            }}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

// --- Start screen ---
const StartScreen = ({ defaultName, onStart, best }) => {
    const [name, setName] = useState(defaultName);
    return (
        <div className="bn-stage-inner" data-testid="start-screen">
            <div className="bn-header">
                <div className="bn-pill" data-testid="brand-pill">
                    <Sparkles size={14} color="#00f0ff" />
                    <span>BlastNova · Puzzle Blast</span>
                </div>
            </div>
            <div className="bn-panel" style={{ padding: "44px 28px" }}>
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                    <h1
                        className="bn-logo"
                        style={{
                            fontSize: "clamp(40px, 9vw, 64px)",
                            margin: 0,
                            lineHeight: 1,
                        }}
                    >
                        BlastNova
                    </h1>
                    <p
                        style={{
                            color: "var(--bn-ink-dim)",
                            marginTop: 8,
                            fontSize: 15,
                            letterSpacing: "0.02em",
                        }}
                    >
                        Drop blocks. Clear lines. Chain combos.
                    </p>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <label className="bn-sr" htmlFor="nickname-input">
                        Nickname
                    </label>
                    <input
                        id="nickname-input"
                        className="bn-input"
                        placeholder="Your nickname"
                        value={name}
                        onChange={(e) => setName(e.target.value.slice(0, 18))}
                        maxLength={18}
                        data-testid="nickname-input"
                    />

                    <button
                        className="bn-btn"
                        onClick={() => {
                            audio.unlock();
                            onStart((name || "").trim() || generateNickname());
                        }}
                        data-testid="play-button"
                    >
                        Start Playing
                    </button>
                </div>

                {best > 0 && (
                    <div
                        style={{
                            marginTop: 22,
                            textAlign: "center",
                            color: "var(--bn-ink-dim)",
                            fontSize: 13,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                        }}
                        data-testid="best-score-display"
                    >
                        <Trophy size={14} color="#ffb547" /> Best Score:{" "}
                        <span
                            className="bn-mono"
                            style={{ color: "#fff", fontWeight: 700 }}
                        >
                            {best}
                        </span>
                    </div>
                )}
            </div>

            <div
                style={{
                    color: "var(--bn-ink-faint)",
                    fontSize: 12,
                    textAlign: "center",
                    padding: "10px 4px 0",
                    lineHeight: 1.6,
                }}
            >
                Drag a block from the tray onto the 8×8 board. Fill rows or
                columns to clear them. Clear multiple lines at once for combo
                bonuses. Game ends when no piece fits.
            </div>
        </div>
    );
};

// --- Main game ---
export default function BlastNova() {
    const [phase, setPhase] = useState("start"); // start | play | over
    const [nickname, setNickname] = useState(
        () => readWithLegacy(LS_NAME, LEGACY_NAME) || generateNickname()
    );
    const [muted, setMuted] = useState(
        () => readWithLegacy(LS_MUTE, LEGACY_MUTE) === "1"
    );

    const [board, setBoard] = useState(makeEmptyBoard);
    const [tray, setTray] = useState(() =>
        createTray(makeEmptyBoard(), Math.random)
    );
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(
        () => Number(readWithLegacy(LS_BEST, LEGACY_BEST) || 0)
    );
    const [combo, setCombo] = useState(0);
    const [comboPop, setComboPop] = useState(null);
    const [scoreBump, setScoreBump] = useState(false);
    const [boardFlash, setBoardFlash] = useState(false);
    const [clearing, setClearing] = useState(new Set());
    const [popCells, setPopCells] = useState(new Set());
    const [shake, setShake] = useState(false);

    const boardRef = useRef(null);
    const [drag, setDrag] = useState(null);

    useEffect(() => {
        audio.setMuted(muted);
        localStorage.setItem(LS_MUTE, muted ? "1" : "0");
    }, [muted]);

    useEffect(() => {
        if (score > best) {
            setBest(score);
            localStorage.setItem(LS_BEST, String(score));
        }
    }, [score, best]);

    useEffect(() => {
        if (nickname) localStorage.setItem(LS_NAME, nickname);
    }, [nickname]);

    const startGame = useCallback((name) => {
        setNickname(name);
        setBoard(makeEmptyBoard());
        setTray(createTray(makeEmptyBoard(), Math.random));
        setScore(0);
        setCombo(0);
        setClearing(new Set());
        setPopCells(new Set());
        setPhase("play");
    }, []);

    const getCellSize = useCallback(() => {
        const el = boardRef.current;
        if (!el) return 48;
        const rect = el.getBoundingClientRect();
        const { gap, padding } = readBoardGapPadding(el);
        return (rect.width - padding * 2 - gap * (BOARD_SIZE - 1)) / BOARD_SIZE;
    }, []);

    const handlePointerDown = useCallback(
        (e, piece) => {
            if (phase !== "play") return;
            const slotIndex = tray.findIndex(
                (p) => p && p.uid === piece.uid
            );
            if (slotIndex === -1) return;

            e.preventDefault();
            audio.pickup();

            const cellSize = getCellSize();
            const pieceRows = piece.cells.length;
            const isTouch = e.pointerType === "touch";

            // Anchor pointer at center of piece's TOP-LEFT cell so wherever the
            // pointer is, that's exactly where the piece's first cell lands.
            const offsetX = cellSize / 2;
            const offsetY = isTouch ? pieceRows * cellSize + 24 : cellSize / 2;

            setDrag({
                piece,
                slotIndex,
                pos: {
                    x: e.clientX - offsetX,
                    y: e.clientY - offsetY,
                },
                offset: { x: offsetX, y: offsetY },
                cellSize,
                hovered: null,
                valid: false,
            });
        },
        [phase, tray, getCellSize]
    );

    const computeHoveredFromPreview = useCallback(
        (previewLeft, previewTop, piece, cellSize) => {
            const el = boardRef.current;
            if (!el) return null;
            const rect = el.getBoundingClientRect();
            const { gap, padding } = readBoardGapPadding(el);
            const step = cellSize + gap;

            const pieceCols = piece.cells[0].length;
            const pieceRows = piece.cells.length;

            const previewRight = previewLeft + pieceCols * cellSize;
            const previewBottom = previewTop + pieceRows * cellSize;

            const overlaps =
                previewRight > rect.left &&
                previewLeft < rect.right &&
                previewBottom > rect.top &&
                previewTop < rect.bottom;
            if (!overlaps) return null;

            const px = previewLeft - rect.left - padding;
            const py = previewTop - rect.top - padding;

            const col = Math.round(px / step);
            const row = Math.round(py / step);

            const r = Math.max(0, Math.min(BOARD_SIZE - pieceRows, row));
            const c = Math.max(0, Math.min(BOARD_SIZE - pieceCols, col));
            return { row: r, col: c };
        },
        []
    );

    useEffect(() => {
        if (!drag) return;

        const handleMove = (e) => {
            const piece = drag.piece;
            const cellSize = drag.cellSize;
            const previewLeft = e.clientX - drag.offset.x;
            const previewTop = e.clientY - drag.offset.y;

            const hovered = computeHoveredFromPreview(
                previewLeft,
                previewTop,
                piece,
                cellSize
            );
            const valid = hovered
                ? canPlace(board, piece.cells, hovered.row, hovered.col)
                : false;

            setDrag((d) =>
                d
                    ? {
                          ...d,
                          pos: { x: previewLeft, y: previewTop },
                          hovered,
                          valid,
                      }
                    : d
            );
        };

        const handleUp = () => {
            // Do NOT run commitPlacement inside a state-updater (StrictMode
            // double-invokes updaters → double-applied placement+score).
            const d = drag;
            if (d && d.hovered && d.valid) {
                commitPlacement(d.piece, d.slotIndex, d.hovered);
            } else if (d) {
                audio.invalid();
            }
            setDrag(null);
        };

        window.addEventListener("pointermove", handleMove, { passive: false });
        window.addEventListener("pointerup", handleUp);
        window.addEventListener("pointercancel", handleUp);
        return () => {
            window.removeEventListener("pointermove", handleMove);
            window.removeEventListener("pointerup", handleUp);
            window.removeEventListener("pointercancel", handleUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drag, board, computeHoveredFromPreview]);

    const commitPlacement = useCallback(
        (piece, slotIndex, target) => {
            const placed = placeShape(
                board,
                piece.cells,
                target.row,
                target.col,
                piece.theme
            );
            const newCells = new Set();
            for (let r = 0; r < piece.cells.length; r++) {
                for (let c = 0; c < piece.cells[0].length; c++) {
                    if (piece.cells[r][c]) {
                        newCells.add(`${target.row + r},${target.col + c}`);
                    }
                }
            }

            setBoard(placed);
            setPopCells(newCells);
            setTimeout(() => setPopCells(new Set()), 300);
            audio.place();

            const newTrayPieces = tray.map((p, i) =>
                i === slotIndex ? null : p
            );
            const refilled = newTrayPieces.every((p) => p === null)
                ? createTray(placed, Math.random)
                : newTrayPieces;
            setTray(refilled);

            const { fullRows, fullCols } = findFullLines(placed);
            const linesCleared = fullRows.length + fullCols.length;
            const clearCoords = getClearCoords(fullRows, fullCols);
            const cellsCleared = clearCoords.size;

            const piecePoints = piece.cells.flat().filter(Boolean).length;
            let nextCombo = combo;

            if (linesCleared > 0) {
                nextCombo = combo + 1;
                setClearing(clearCoords);
                audio.clear(linesCleared);
                if (linesCleared >= 2 || nextCombo >= 2) {
                    audio.combo(Math.max(linesCleared - 1, nextCombo - 1));
                    setShake(true);
                    setTimeout(() => setShake(false), 380);
                    setBoardFlash(true);
                    setTimeout(() => setBoardFlash(false), 400);
                    let text;
                    if (linesCleared >= 4) text = "MEGA BLAST!";
                    else if (linesCleared === 3) text = "SUPER COMBO!";
                    else if (linesCleared === 2) text = "COMBO!";
                    else text = `${nextCombo}× CHAIN!`;
                    const id = Math.random().toString(36).slice(2);
                    setComboPop({ text, id });
                    setTimeout(
                        () =>
                            setComboPop((p) =>
                                p && p.id === id ? null : p
                            ),
                        1100
                    );
                }
                setTimeout(() => {
                    setBoard((b) => applyClears(b, clearCoords));
                    setClearing(new Set());
                    setTimeout(() => {
                        checkGameOver(refilled);
                    }, 30);
                }, 460);
            } else {
                nextCombo = 0;
                setTimeout(() => checkGameOver(refilled), 60);
            }

            const earned = computeScore({
                piecePoints,
                linesCleared,
                cellsCleared,
                comboStreak: nextCombo,
            });
            setScore((s) => s + earned);
            setScoreBump(true);
            setTimeout(() => setScoreBump(false), 360);
            setCombo(nextCombo);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [board, tray, combo]
    );

    const checkGameOver = useCallback((currentTray) => {
        setBoard((currentBoard) => {
            if (!hasAnyValidMove(currentBoard, currentTray)) {
                audio.gameover();
                setPhase("over");
            }
            return currentBoard;
        });
    }, []);

    const restart = useCallback(() => {
        setBoard(makeEmptyBoard());
        setTray(createTray(makeEmptyBoard(), Math.random));
        setScore(0);
        setCombo(0);
        setClearing(new Set());
        setPopCells(new Set());
        setPhase("play");
    }, []);

    const cellsRender = useMemo(() => {
        const ghostCells = new Set();
        let ghostValid = false;
        if (drag && drag.hovered) {
            const { row, col } = drag.hovered;
            for (let r = 0; r < drag.piece.cells.length; r++) {
                for (let c = 0; c < drag.piece.cells[0].length; c++) {
                    if (drag.piece.cells[r][c]) {
                        ghostCells.add(`${row + r},${col + c}`);
                    }
                }
            }
            ghostValid = drag.valid;
        }
        return { ghostCells, ghostValid };
    }, [drag]);

    const deadlockFlags = useMemo(() => {
        return tray.map((p) => (p ? !pieceFitsAnywhere(board, p.cells) : false));
    }, [board, tray]);

    return (
        <div className="bn-stage" data-testid="bn-stage">
            {phase === "start" ? (
                <StartScreen
                    defaultName={nickname}
                    onStart={startGame}
                    best={best}
                />
            ) : (
                <div className="bn-stage-inner">
                    <header className="bn-header">
                        <div className="bn-pill" data-testid="player-pill">
                            <Sparkles size={14} color="#00f0ff" />
                            <span data-testid="nickname-display">
                                {nickname}
                            </span>
                        </div>
                        <div className="bn-header-right">
                            <button
                                className="bn-btn secondary"
                                style={{ padding: "10px 14px" }}
                                onClick={() => setMuted((m) => !m)}
                                aria-label="Toggle sound"
                                data-testid="mute-button"
                            >
                                {muted ? (
                                    <VolumeX size={16} />
                                ) : (
                                    <Volume2 size={16} />
                                )}
                            </button>
                            <button
                                className="bn-btn secondary"
                                style={{ padding: "10px 14px" }}
                                onClick={restart}
                                aria-label="Restart"
                                data-testid="restart-button"
                            >
                                <RotateCcw size={16} />
                            </button>
                        </div>
                    </header>

                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            justifyContent: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <div
                            className={`bn-score-chip${
                                scoreBump ? " is-bump" : ""
                            }`}
                            data-testid="score-chip"
                        >
                            <span className="label">Score</span>
                            <span className="value" data-testid="score-value">
                                {score}
                            </span>
                        </div>
                        <div className="bn-score-chip" data-testid="best-chip">
                            <span className="label">Best</span>
                            <span className="value" data-testid="best-value">
                                {best}
                            </span>
                        </div>
                        <div
                            className="bn-score-chip"
                            data-testid="combo-chip"
                        >
                            <span className="label">Combo</span>
                            <span className="value" data-testid="combo-value">
                                ×{combo}
                            </span>
                        </div>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            position: "relative",
                        }}
                        className={shake ? "bn-shake" : ""}
                    >
                        <div className="bn-board-shell" data-testid="game-board-shell">
                            <div className="bn-board-shell-corner" aria-hidden />
                            <div className="bn-board-shell-top bn-mono" aria-hidden>
                                {Array.from({ length: BOARD_SIZE }, (_, i) => (
                                    <span
                                        key={`col-${i}`}
                                        className="bn-board-axis-label"
                                    >
                                        {i + 1}
                                    </span>
                                ))}
                            </div>
                            <div className="bn-board-shell-side bn-mono" aria-hidden>
                                {Array.from({ length: BOARD_SIZE }, (_, i) => (
                                    <span
                                        key={`row-${i}`}
                                        className="bn-board-axis-label"
                                    >
                                        {i + 1}
                                    </span>
                                ))}
                            </div>
                            <div
                                ref={boardRef}
                                className={`bn-board${boardFlash ? " is-flash" : ""}`}
                                data-testid="game-board"
                            >
                                {board.map((row, r) =>
                                    row.map((cell, c) => {
                                        const key = `${r},${c}`;
                                        const ghost =
                                            cellsRender.ghostCells.has(key);
                                        const ghostInvalid =
                                            ghost && !cellsRender.ghostValid;
                                        const isClearing = clearing.has(key);
                                        const isPop = popCells.has(key);
                                        const style = cell
                                            ? {
                                                  "--cell-color": cell.color,
                                                  "--cell-glow": cell.glow,
                                              }
                                            : undefined;
                                        const cls = [
                                            "bn-cell",
                                            cell ? "is-filled" : "",
                                            !cell && r % 2 === 1
                                                ? "is-alt-row"
                                                : "",
                                            ghost && !cell && !ghostInvalid
                                                ? "is-ghost"
                                                : "",
                                            ghostInvalid && !cell
                                                ? "is-ghost-invalid"
                                                : "",
                                            isClearing ? "is-clearing" : "",
                                            isPop ? "is-pop" : "",
                                        ]
                                            .filter(Boolean)
                                            .join(" ");
                                        return (
                                            <div
                                                key={key}
                                                className={cls}
                                                style={style}
                                                data-testid={`cell-${r}-${c}`}
                                                data-row={r}
                                                data-col={c}
                                            />
                                        );
                                    })
                                )}
                                {comboPop && (
                                    <div
                                        className="bn-combo-pop"
                                        data-testid="combo-pop"
                                    >
                                        {comboPop.text}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bn-tray" data-testid="piece-tray">
                        {tray.map((piece, idx) => (
                            <div
                                key={idx}
                                className="bn-tray-slot"
                                data-testid={`tray-slot-${idx}`}
                            >
                                {drag && drag.slotIndex === idx ? (
                                    <div className="bn-tray-piece is-empty" />
                                ) : (
                                    <TrayPiece
                                        piece={piece}
                                        onPointerDown={handlePointerDown}
                                        deadlocked={deadlockFlags[idx]}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <div
                        style={{
                            color: "var(--bn-ink-faint)",
                            fontSize: 12,
                            textAlign: "center",
                            padding: "4px 4px 0",
                            letterSpacing: "0.02em",
                        }}
                    >
                        Tip: drag the block — release over a valid spot to
                        place. Clearing multiple lines triggers a combo.
                    </div>
                </div>
            )}

            {drag && (
                <DragPreview
                    piece={drag.piece}
                    position={drag.pos}
                    cellSize={drag.cellSize}
                />
            )}

            {phase === "over" && (
                <div className="bn-modal-backdrop" data-testid="gameover-modal">
                    <div className="bn-panel bn-modal">
                        <div
                            style={{
                                color: "var(--bn-ink-faint)",
                                letterSpacing: "0.22em",
                                fontSize: 11,
                                textTransform: "uppercase",
                                marginBottom: 6,
                            }}
                        >
                            Game Over
                        </div>
                        <h2
                            className="bn-display"
                            style={{
                                margin: 0,
                                fontSize: 32,
                                color: "#fff",
                                marginBottom: 18,
                            }}
                        >
                            {score >= best && score > 0
                                ? "New Best!"
                                : "Nice Run"}
                        </h2>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 12,
                                marginBottom: 22,
                            }}
                        >
                            <div className="bn-score-chip">
                                <span className="label">Final</span>
                                <span
                                    className="value"
                                    data-testid="final-score"
                                >
                                    {score}
                                </span>
                            </div>
                            <div className="bn-score-chip">
                                <span className="label">Best</span>
                                <span className="value">{best}</span>
                            </div>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                gap: 10,
                                justifyContent: "center",
                            }}
                        >
                            <button
                                className="bn-btn"
                                onClick={restart}
                                data-testid="play-again-button"
                            >
                                Play Again
                            </button>
                            <button
                                className="bn-btn secondary"
                                onClick={() => setPhase("start")}
                                data-testid="back-to-menu-button"
                            >
                                Menu
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
